/**
 * The decisions the tenancy card makes about the blocks of an application report
 * that describe a tenancy's public face: the certificate its gateway is published
 * under, the budget its anonymous surface holds a caller to, and what each of its
 * roles has actually read of what was rendered for it.
 *
 * Six of them are the reason this file exists.
 *
 * **The names are two sets, and a difference between them is a finding.** A
 * certificate is issued for the tenancy's own domain and for `proxy.<domain>`,
 * and the failure worth catching is a name that is published and not carried.
 * Nothing shows it on the provider's side — the listener binds, the gateway
 * serves — so it comes out as clients refusing one of the two names. Both sets
 * therefore leave here whole, and agreement is a state of its own rather than
 * the absence of a warning.
 *
 * **A certificate from the test authority is not a fault.** `publicly_trusted:
 * false` is what the test authority honestly produces: the TLS is real, and a
 * client that was never told about the root refuses it. So it is neither red nor
 * passed off as the live one — it says which it is, and what that costs.
 * `null` is a third answer and means the control plane did not install the pair
 * and claims to know nothing about the authority that did.
 *
 * **`kept` is the wanted outcome, not an empty one.** It says the authority was
 * not asked at all. The limits are counted against the provider's registered
 * domain and shared by every tenancy, so reusing a valid certificate is the
 * thing that keeps a fleet inside them; a pass that asked again would be the
 * fault. It reads as normal here, and the wording says why.
 *
 * **A failure with the next attempt named is a wait.** The wait starts at
 * fifteen minutes and doubles to a day at most, and a renewal begins a third of
 * a lifetime before expiry, so a failing renewal shows for weeks before
 * anything stops working. Drawn as a breakage it would be acted on as an
 * outage; drawn as nothing it would be found by a customer. Only a failure with
 * no next attempt named reads as broken.
 *
 * **The budget is a statement about a process.** gdsgate reads `[proxy]` once,
 * when it starts, so the numbers being written and the numbers being in effect
 * are two events. The server answers which by comparing what was running when
 * the numbers were written against what is running now, so `in_force` means the
 * gateway started since and read them, and `not_read_yet` means it is the very
 * same process and is proved not to have. That one is a finding and is shown
 * rather than tidied away; `not_known` is not one — it is an absence of
 * knowledge, and colouring it like a proved hole would make the two read alike.
 *
 * **A value waiting for a start is named and never printed.** The same question
 * stands for every value of both rendered files, and the server answers it per
 * role with the names of the values the running process cannot have read. Names
 * are what a person acts on, and the same field would carry a secret the day one
 * of those values is one — so what leaves here is the name and nothing after it,
 * whatever the server puts in the string.
 *
 * The contract is `gdsgate-saas`, docs/api.md §6 (the report) and §6d (the
 * certificate).
 */

import type {
  AnonymousBudget,
  CertificateReport,
  ReconcileReport,
  RoleReading,
} from '../../api/types';
import type { SimpleKey } from '../../i18n/types';
import type { AlertTone } from '../../ui/Alert';
import type { Tone } from '../../ui/StatusBadge';

// ---------------------------------------------------------------------------
// Getting at the blocks
// ---------------------------------------------------------------------------

/**
 * A block of the report, or the reason there is none — and the two are not the
 * same reason.
 *
 * A tenancy nothing has been applied to has never had its certificate looked
 * at; a report from before the certificate step is a report that says nothing
 * about one. Neither is an occasion to draw a certificate this console has not
 * been told about.
 */
export type Block<T> = { present: true; value: T } | { present: false; reasonKey: SimpleKey };

export function certificateBlock(report: ReconcileReport | undefined): Block<CertificateReport> {
  if (report === undefined) return { present: false, reasonKey: 'certificate.absent.noRun' };
  const certificate = report.certificate;
  if (certificate === undefined) return { present: false, reasonKey: 'certificate.absent.noBlock' };
  return { present: true, value: certificate };
}

export function budgetBlock(report: ReconcileReport | undefined): Block<AnonymousBudget> {
  if (report === undefined) return { present: false, reasonKey: 'budget.absent.noRun' };
  const budget = report.anonymous_budget;
  if (budget === undefined) return { present: false, reasonKey: 'budget.absent.noBlock' };
  return { present: true, value: budget };
}

/**
 * Where every role's values stand, or the reason the report does not say.
 *
 * An older report carries no such block, and that is not the same as a report
 * saying every value has been read: what those roles are running is not known
 * from it, and the block says so rather than drawing two roles in the clear.
 */
export function settingsBlock(report: ReconcileReport | undefined): Block<RoleReading[]> {
  if (report === undefined) return { present: false, reasonKey: 'settings.absent.noRun' };
  const settings = report.rendered_settings;
  if (settings === undefined) return { present: false, reasonKey: 'settings.absent.noBlock' };
  return { present: true, value: settings.roles };
}

/**
 * A field the server sends as `null` when it has nothing to put there.
 *
 * `undefined` for an older report that left the field out altogether: both are
 * "there is nothing here", and neither may become a date on screen.
 */
export function filled(value: string | null | undefined): string | undefined {
  if (value === null || value === undefined || value === '') return undefined;
  return value;
}

// ---------------------------------------------------------------------------
// The names, both sets of them
// ---------------------------------------------------------------------------

/** As both sides compare them: case-insensitive, a trailing dot dropped (§6d). */
function normalised(name: string): string {
  return name.trim().replace(/\.$/, '').toLowerCase();
}

/**
 * How the set the tenancy is published under and the set the certificate
 * carries stand to each other.
 *
 * Three answers, and `none` is separate on purpose: an empty certificate agrees
 * with nothing, and folding it into agreement is exactly how a tenancy with no
 * certificate at all would read as a tenancy in order.
 */
export type NameStanding =
  | { kind: 'none'; wanted: string[] }
  | { kind: 'agree'; names: string[] }
  | {
      kind: 'diverged';
      wanted: string[];
      installed: string[];
      /** Published and not covered. This is the half that fails the step. */
      missing: string[];
      /** Carried and not published. Not a fault; part of the difference all the same. */
      extra: string[];
    };

export function nameStanding(certificate: CertificateReport): NameStanding {
  const wanted = certificate.wanted;
  const installed = certificate.installed;

  if (installed.length === 0) return { kind: 'none', wanted };

  const carried = new Set(installed.map(normalised));
  const published = new Set(wanted.map(normalised));
  const uncovered = wanted.filter((name) => !carried.has(normalised(name)));
  const extra = installed.filter((name) => !published.has(normalised(name)));

  if (uncovered.length === 0 && extra.length === 0) return { kind: 'agree', names: wanted };

  return {
    kind: 'diverged',
    wanted,
    installed,
    // The server's own list wherever it has one — it is the finding, in the
    // words the step refused with. Worked out here only if the answer left it
    // empty while the sets differ, so that a difference is never shown without
    // the half that says which names are not covered.
    missing: certificate.missing.length > 0 ? certificate.missing : uncovered,
    extra,
  };
}

/**
 * Whether this tenancy has a certificate at all, and therefore whether a connector
 * at the customer's own site has anywhere to register.
 *
 * The consequence is named where the certificate is shown because it is
 * invisible anywhere else: without a certificate the registration relay is
 * neither bound nor published, and the control plane withholds the address
 * rather than not knowing it — the registration listener answers in cleartext,
 * so naming it would mean sending a one-time join token across the internet in
 * the clear (docs/api.md §6, `registration.offsite`). An operator who does not
 * read that here reads it as "the customer's connectors do not come up".
 */
export function relayPublished(certificate: CertificateReport): boolean {
  return certificate.installed.length > 0;
}

// ---------------------------------------------------------------------------
// What a client makes of it
// ---------------------------------------------------------------------------

export type Trust = 'publicly_trusted' | 'test_authority' | 'not_said';

export function trustOf(certificate: CertificateReport): Trust {
  if (certificate.publicly_trusted === true) return 'publicly_trusted';
  if (certificate.publicly_trusted === false) return 'test_authority';
  return 'not_said';
}

/**
 * How each answer is coloured, and none of the three is red.
 *
 * The test authority is deliberately not a warning: it answers this way by
 * design, and a deployment testing itself against it would be flagged forever.
 * It is deliberately not the green of a publicly trusted one either — the whole
 * point of showing it is that a customer's connector will refuse this certificate
 * unless somebody tells it about the root.
 */
const TRUST_TONE: Record<Trust, Tone> = {
  publicly_trusted: 'ok',
  test_authority: 'info',
  not_said: 'neutral',
};

const TRUST_LABEL: Record<Trust, SimpleKey> = {
  publicly_trusted: 'certificate.trust.trusted',
  test_authority: 'certificate.trust.test',
  not_said: 'certificate.trust.notSaid',
};

const TRUST_MEANING: Record<Trust, SimpleKey> = {
  publicly_trusted: 'certificate.trust.trusted.meaning',
  test_authority: 'certificate.trust.test.meaning',
  not_said: 'certificate.trust.notSaid.meaning',
};

export function trustTone(trust: Trust): Tone {
  return TRUST_TONE[trust];
}

export function trustLabelKey(trust: Trust): SimpleKey {
  return TRUST_LABEL[trust];
}

export function trustMeaningKey(trust: Trust): SimpleKey {
  return TRUST_MEANING[trust];
}

// ---------------------------------------------------------------------------
// How long it lasts, and what is being waited for
// ---------------------------------------------------------------------------

/**
 * The two dates, both of them.
 *
 * `renewAt` is the more useful and the less obvious: the threshold is worked out
 * from the certificate's own lifetime rather than from a number of days, so it
 * is not something this console could compute beside the expiry even if it
 * wanted to. Absent means there is no certificate to read a date off, and
 * absent is what it stays.
 */
export interface Validity {
  notAfter?: string;
  renewAt?: string;
}

export function validityOf(certificate: CertificateReport): Validity {
  const validity: Validity = {};
  const notAfter = filled(certificate.not_after);
  const renewAt = filled(certificate.renew_at);
  if (notAfter !== undefined) validity.notAfter = notAfter;
  if (renewAt !== undefined) validity.renewAt = renewAt;
  return validity;
}

/** Whether either date is there at all. */
export function dated(validity: Validity): boolean {
  return validity.notAfter !== undefined || validity.renewAt !== undefined;
}

/**
 * Where the attempts stand: nothing has failed, a failure with the next attempt
 * named, or a failure with none.
 *
 * The middle one is the whole point. A renewal that keeps failing is visible for
 * weeks before anything expires, and what a person needs from the screen is the
 * count and the time of the next go — not a red box on every pass of a loop that
 * is behaving exactly as designed.
 */
export type Attempts =
  | { kind: 'clear' }
  | { kind: 'waiting'; attempts: number; retryAfter: string }
  | { kind: 'stalled'; attempts: number };

export function attemptsOf(certificate: CertificateReport): Attempts {
  const retryAfter = filled(certificate.retry_after);
  if (retryAfter !== undefined) {
    return { kind: 'waiting', attempts: certificate.attempts, retryAfter };
  }
  if (certificate.attempts > 0) return { kind: 'stalled', attempts: certificate.attempts };
  return { kind: 'clear' };
}

/**
 * A pass that tried and failed, with the next attempt already scheduled.
 *
 * Shown as the wait it is rather than as the failure it also is: the outcome
 * alone cannot say this, which is why the whole block decides how it is drawn.
 */
export function waitingAgain(certificate: CertificateReport): boolean {
  return certificate.outcome === 'failed' && attemptsOf(certificate).kind === 'waiting';
}

// ---------------------------------------------------------------------------
// The outcome
// ---------------------------------------------------------------------------

/**
 * How each outcome is coloured.
 *
 * `kept` is green, and that is the substance of it rather than decoration: not
 * asking the authority is the behaviour that keeps every tenancy of this
 * deployment inside limits counted against one registered domain. `foreign` and
 * `untended` are neutral — both are states somebody chose, and neither is this
 * control plane's to put right.
 */
const OUTCOME_TONE: Record<string, Tone> = {
  kept: 'ok',
  obtained: 'ok',
  waiting: 'warn',
  failed: 'deny',
  foreign: 'neutral',
  untended: 'neutral',
};

const OUTCOME_LABEL: Record<string, SimpleKey> = {
  kept: 'certificate.outcome.kept',
  obtained: 'certificate.outcome.obtained',
  waiting: 'certificate.outcome.waiting',
  failed: 'certificate.outcome.failed',
  foreign: 'certificate.outcome.foreign',
  untended: 'certificate.outcome.untended',
};

const OUTCOME_MEANING: Record<string, SimpleKey> = {
  kept: 'certificate.outcome.kept.meaning',
  obtained: 'certificate.outcome.obtained.meaning',
  waiting: 'certificate.outcome.waiting.meaning',
  failed: 'certificate.outcome.failed.meaning',
  foreign: 'certificate.outcome.foreign.meaning',
  untended: 'certificate.outcome.untended.meaning',
};

export function outcomeTone(certificate: CertificateReport): Tone {
  if (waitingAgain(certificate)) return 'warn';
  return OUTCOME_TONE[certificate.outcome] ?? 'neutral';
}

export function outcomeLabelKey(certificate: CertificateReport): SimpleKey {
  if (waitingAgain(certificate)) return 'certificate.outcome.waiting';
  return OUTCOME_LABEL[certificate.outcome] ?? 'certificate.outcome.unknown';
}

export function outcomeMeaningKey(certificate: CertificateReport): SimpleKey {
  if (waitingAgain(certificate)) return 'certificate.outcome.waiting.meaning';
  return OUTCOME_MEANING[certificate.outcome] ?? 'certificate.outcome.unknown.meaning';
}

/**
 * Whether the outcome is the ordinary one — the card states it, and its reason
 * belongs behind a disclosure rather than in a paragraph that is always there.
 *
 * `kept` and `obtained` are the two: nothing is wrong and nobody has to act.
 * Everything else keeps its words on the card, including the two nobody chose
 * to be wrong — `foreign` and `untended` are somebody's decision, and an
 * operator who does not know which one this tenancy is on cannot read the rest
 * of the card. The green outcomes still say what they are on the badge, so
 * `kept` reads as a state and never as an empty box.
 */
export function outcomeIsOrdinary(certificate: CertificateReport): boolean {
  return outcomeTone(certificate) === 'ok';
}

/**
 * The same rule for what a client makes of the certificate: only "a client
 * already trusts this" needs no saying. The test authority and a pair this
 * control plane did not install both change what somebody has to do next.
 */
export function trustIsOrdinary(trust: Trust): boolean {
  return trustTone(trust) === 'ok';
}

// ---------------------------------------------------------------------------
// Where a rendered value stands: the anonymous surface, and every other value
// ---------------------------------------------------------------------------

/**
 * The five states the server answers with, in the order they read: proved, proved
 * against, waiting, unanswerable, nothing written.
 *
 * Listed so that the console can be held to drawing all five differently. There
 * is no sixth, and there is no `from_the_first_start` — a pass that stands a
 * tenancy up answers `in_force`, because its gateway did start.
 */
export const SETTINGS_STATES = [
  'in_force',
  'not_read_yet',
  'at_the_next_start',
  'not_known',
  'not_written',
] as const;

/**
 * How each state is coloured, and none of the five is red.
 *
 * There are more states than there are tones, so the colour carries the loudness
 * and the words carry the state. What the colour must get right is the one
 * distinction the server went to the trouble of making:
 *
 * - `not_read_yet` is **proved**, and it is the finding. A gateway that is the
 *   very process which was running when the numbers were written has not read
 *   them and will not until it starts again;
 * - `not_known` is **not** proved and must not borrow that warning. Either the
 *   runtime could not be asked or there is no witness of the write — an absence
 *   of knowledge, not a hole, and the role's next start settles it. It is said
 *   out loud all the same, because a number that may not be in force is not the
 *   same as one that is;
 * - `at_the_next_start` warns because a gateway that is not running is not a
 *   gateway in good order, whatever it would read if it started;
 * - `not_written` warns about the **pass**: the step did not run, so this report
 *   is not the one to read the tenancy's state off at all.
 */
const SETTINGS_TONE: Record<string, Tone> = {
  in_force: 'ok',
  not_read_yet: 'warn',
  at_the_next_start: 'warn',
  not_known: 'info',
  not_written: 'warn',
};

const SETTINGS_LABEL: Record<string, SimpleKey> = {
  in_force: 'settings.state.inForce',
  not_read_yet: 'settings.state.notReadYet',
  at_the_next_start: 'settings.state.atTheNextStart',
  not_known: 'settings.state.notKnown',
  not_written: 'settings.state.notWritten',
};

/**
 * What each state means for the anonymous surface in particular, which is not
 * what it means in general: here the value is a limiter on the two calls that
 * answer before there is anybody to recognise, so "not read" has a consequence
 * worth a sentence of its own.
 */
const BUDGET_MEANING: Record<string, SimpleKey> = {
  in_force: 'budget.state.inForce.meaning',
  not_read_yet: 'budget.state.notReadYet.meaning',
  at_the_next_start: 'budget.state.atTheNextStart.meaning',
  not_known: 'budget.state.notKnown.meaning',
  not_written: 'budget.state.notWritten.meaning',
};

export function readingTone(state: string): Tone {
  return SETTINGS_TONE[state] ?? 'neutral';
}

export function readingLabelKey(state: string): SimpleKey {
  return SETTINGS_LABEL[state] ?? 'settings.state.unknown';
}

export function budgetMeaningKey(state: string): SimpleKey {
  return BUDGET_MEANING[state] ?? 'budget.state.unknown.meaning';
}

/**
 * Whether the state is a finding — something proved, that somebody has to act on.
 *
 * Exactly one of the five is. This is the difference between "we know this number
 * is not in force" and "we do not know whether it is", and it is kept as a
 * separate answer from the colour because a warning is not automatically a
 * finding: `not_known` and `at_the_next_start` are both worth saying and neither
 * is proof of anything.
 */
export function readingFinding(state: string): boolean {
  return state === 'not_read_yet';
}

/**
 * Whether this state has to be said out loud, over and above the numbers.
 *
 * `in_force` is the only one that has not: the gateway started since the numbers
 * were written, so it is running them and there is nothing for anybody to do.
 * Every other state — including one this console has never heard of — gets a
 * paragraph, because each of the others means the tenancy is not, or may not be,
 * under the number the card is showing.
 */
export function budgetNeedsSaying(state: string): boolean {
  return state !== 'in_force';
}

/**
 * The same judgement in the tones an explanatory box has, which has no green.
 *
 * The proved states and the pass that wrote nothing keep the warning they have on
 * the badge; not knowing keeps its own quieter mark; a state nobody knows takes
 * neither.
 */
export function budgetAlertTone(state: string): AlertTone {
  const tone = readingTone(state);
  if (tone === 'warn') return 'warn';
  if (tone === 'info' || tone === 'ok') return 'info';
  return 'neutral';
}

/**
 * The values a role has not read, **by name and by nothing else**.
 *
 * The server sends names today — `[proxy].public_anonymous_burst`, in the
 * spelling of gdsgate's own diagnostics — and a name is the whole of what an
 * operator needs. Anything after an `=` is dropped rather than trusted: the
 * report is shown to whoever may read a tenancy's card, the fields these names
 * point at are limits now and could be a secret later, and a console that printed
 * whatever arrived would be the thing that leaked it. Empty entries and
 * duplicates go, so a list is drawn only when there is something in it.
 */
export function unreadNames(unread: readonly string[]): string[] {
  const names: string[] = [];
  for (const entry of unread) {
    const name = entry.split('=')[0]?.trim() ?? '';
    if (name !== '' && !names.includes(name)) names.push(name);
  }
  return names;
}

/** The roles whose settings the console has words for. Anything else prints as it came. */
const ROLE_LABEL: Record<string, SimpleKey> = {
  authority: 'settings.role.authority',
  proxy: 'settings.role.proxy',
};

/**
 * The words for one role's name, or nothing when the server names a role this
 * console has never heard of — then its own name is printed, which is more use
 * than "unknown role".
 */
export function roleLabelKey(role: string): SimpleKey | undefined {
  return ROLE_LABEL[role];
}

/**
 * The roles in the order the block draws them: whichever has something waiting
 * comes first, because that is what somebody opened the block for.
 *
 * Both roles are always drawn — the server sends both so that nobody has to
 * decide whether a missing role means "nothing pending" or "not looked at", and
 * dropping the quiet one here would undo that. "Something waiting" is counted the
 * way the list is drawn, so the order and the list can never disagree.
 */
export function readingOrder(roles: readonly RoleReading[]): RoleReading[] {
  const waiting = (role: RoleReading) => Number(unreadNames(role.unread).length > 0);
  return [...roles].sort((left, right) => {
    const pending = waiting(right) - waiting(left);
    if (pending !== 0) return pending;
    return left.role.localeCompare(right.role);
  });
}
